import useSWR from 'swr';
import { Star, ShoppingCart } from 'lucide-react';
import { API } from '@/lib/api';
import { useCart, formatAUD } from '@/context/CartContext';
import { resolveImage } from '@/lib/resolveImage';

const fetcher = (url) => fetch(`${API}${url}`).then((r) => r.ok ? r.json() : null);

export default function ChefsSpecialBanner() {
  const { data: special } = useSWR('/shop/chefs-special', fetcher);
  const { add } = useCart();

  if (!special?.product) return null;
  const p = special.product;
  const pct = Math.round((special.discount_pct || 0.15) * 100);
  const weekEnd = special.week_end ? new Date(special.week_end) : null;

  const onAdd = () => {
    add({
      id: p.id, name: p.name, category: p.category, unit: p.unit,
      price: p.price, min_qty: p.min_qty, step_qty: p.step_qty, image_url: p.image_url,
    });
  };

  return (
    <section
      data-testid="chefs-special-banner"
      className="border-b border-white/10"
    >
      <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16 py-6 grid grid-cols-1 md:grid-cols-[220px_1fr_auto] gap-6 items-center">
        <div className="relative aspect-square overflow-hidden border border-[var(--mw-primary)]/40 max-w-[220px]">
          <img
            src={resolveImage(p.image_url, 480)}
            alt={p.name}
            width="220"
            height="220"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute top-2 left-2 bg-[var(--mw-primary)] text-white text-[9px] uppercase tracking-[0.22em] px-2 py-1 flex items-center gap-1">
            <Star size={10} fill="currentColor" /> Chef&apos;s Special
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-1.5">
            This week only · {pct}% off
          </div>
          <h2 className="font-display font-light text-3xl sm:text-4xl leading-tight tracking-tight">
            {p.name}
          </h2>
          <p className="text-white/60 leading-relaxed mt-2 max-w-xl">
            {p.description}
          </p>
          <div className="mt-3 flex items-baseline gap-3 flex-wrap">
            <span className="font-display text-3xl text-white tabular-nums">{formatAUD(special.effective_price)}</span>
            <span className="text-white/45 text-sm line-through tabular-nums">{formatAUD(p.price)}</span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-emerald-400">Save {formatAUD(special.savings_per_unit)}/{p.unit}</span>
          </div>
          {weekEnd && (
            <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-white/40">
              Ends {weekEnd.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short' })}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onAdd}
          data-testid="chefs-special-add"
          className="inline-flex items-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] text-white text-[11px] uppercase tracking-[0.22em] px-6 py-4 transition-colors whitespace-nowrap"
        >
          <ShoppingCart size={14} />
          Add to cart
        </button>
      </div>
    </section>
  );
}
